// Import Three.js and related modules
import * as THREE from 'three';
import ViewCubeControls from "../Compass/ViewCubeControls";
import { FACES } from '../Global/materials';
import CompassRingController from '../Compass/CompassRingControls';
import { moveCube, moveRing } from '../Compass/Helpers';
import ZoomButtonsControls from '../Compass/ZoomButtonsControls';
// import AngleBarControls from '../Compass/AngleBarControls';

export class CubeManager {
    constructor(camera, controls) {
        this.camera = camera;
        this.controls = controls;
        this.container = null;

        // Bind all relevant methods to 'this' to avoid losing context
        this.controlChange = this.controlChange.bind(this);
        this.cubeChange = this.cubeChange.bind(this);
        this.compassChange = this.compassChange.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);

        this.initialize(); // Setup all components
        return this.getCubeSceneObjects(); // Return references to cube scene objects
    }

    initialize() {
        // Set up a transparent renderer for the cube UI
        this.cubeRenderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        this.cubeRenderer.setPixelRatio(window.devicePixelRatio * 1.5);
        this.cubeRenderer.setSize(210, 210);

        // Create cube scene and camera
        this.cubeScene = new THREE.Scene();
        this.cubeScene.background = null;

        this.cubeCamera = new THREE.PerspectiveCamera(50, 1, 0.1, 1000);
        this.cubeCamera.position.set(0, 0, 110); // Pull camera back from cube center
        this.cubeCamera.lookAt(0, 0, 0); // Center it on the cube

        // Quaternion used to align cube orientation with scene camera
        const quat = this.camera.quaternion.clone();
        const initialQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI / 2, 0, 0));

        // Initialize and add compass ring
        this.compassRing = new CompassRingController(this.cubeCamera, this.cubeRenderer.domElement);
        const compass = this.compassRing.getObject();
        this.cubeScene.add(compass);

        // Initialize and add view cube
        this.viewCube = new ViewCubeControls(this.cubeCamera, undefined, this.cubeRenderer.domElement);
        this.viewCube.setQuaternion(quat.invert().multiply(initialQuat));
        const cube = this.viewCube.getObject();
        this.cubeScene.add(cube);

        // Optional: angle bar setup (commented out)
        // this.angleBar = new AngleBarControls(this.cubeCamera, this.cubeRenderer.domElement);
        // const {angleBar, angleBox} = this.angleBar.getObjects();
        // this.cubeScene.add(angleBar);
        // this.cubeScene.add(angleBox);

        // Add zoom buttons
        const zoomButtons = new ZoomButtonsControls(this.cubeCamera, this.cubeRenderer.domElement, this.controls);
        const buttons = zoomButtons.getObject();
        this.cubeScene.add(buttons);

        // Register event listeners
        this.controls.addEventListener('change', this.controlChange);
        this.viewCube.addEventListener('angle-change', ({ quaternion, face, angle }) => this.cubeChange(quaternion, face, angle));
        this.compassRing.addEventListener('compass-angle-change', ({ face, angle }) => this.compassChange(face, angle));

        // Mouse interaction for rotating view via cube
        this.cubeRenderer.domElement.addEventListener('mousedown', (event) => this.handleMouseDown(cube, event));
    }

    // Handle mouse down events on the view cube
    handleMouseDown(cube, event) {
        if (event.button === 0) { // Left click only
            const x = (event.offsetX / event.target.clientWidth) * 2 - 1;
            const y = -(event.offsetY / event.target.clientHeight) * 2 + 1;
            const raycaster = new THREE.Raycaster();
            raycaster.setFromCamera({ x, y }, this.cubeCamera);

            // Detect if user clicked on any cube face
            const intersects = raycaster.intersectObjects(cube.children, true);
            if (intersects.length) {
                this.controls.rotateStart(event); // Begin rotating camera
                window.addEventListener('mousemove', this.handleMouseMove);
                window.addEventListener('mouseup', this.handleMouseUp);
            }
        }
    }

    // Rotate scene camera when dragging over the cube
    handleMouseMove(event) {
        this.controls.rotate(event);
    }

    // Stop camera rotation on mouse up
    handleMouseUp() {
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
    }

    // When camera is moved manually (OrbitControls), update cube and compass orientations
    controlChange() {
        const quat = this.camera.quaternion.clone();
        moveCube(quat, this.viewCube);
        moveRing(quat, this.viewCube, this.compassRing);
    }

    // When user clicks on a cube face, rotate camera accordingly
    cubeChange(quaternion, face, angle) {
        // Prevent loop of event triggers
        this.controls.removeEventListener('change', this.controlChange);
        this.compassRing.removeEventListener('compass-angle-change', this.compassChange);

        // Adjust camera angle based on face and angle
        if (face === FACES.top || face === FACES.threed) {
            this.controls.setPhi(angle); // vertical rotation
        } else {
            this.controls.setTheta(angle); // horizontal rotation
        }
        this.controls.update();

        // Update compass to reflect new view
        const quat = quaternion.clone();
        moveRing(quat, this.viewCube, this.compassRing);

        // Reattach event listeners
        this.controls.addEventListener('change', this.controlChange);
        this.compassRing.addEventListener('compass-angle-change', this.compassChange);
    }

    // When user rotates via compass ring, update the view cube and camera
    compassChange(face, angle) {
        this.controls.removeEventListener('change', this.controlChange);
        this.viewCube.removeEventListener('cube-change', this.cubeChange);

        this.controls.setTheta(angle);
        this.controls.update();

        const quat = this.camera.quaternion.clone();
        moveCube(quat, this.viewCube);

        this.controls.addEventListener('change', this.controlChange);
        this.viewCube.addEventListener('cube-change', this.cubeChange);
    }

    // Resize handler: reposition cube overlay when viewport changes
    handleResize() {
        const compassRightOffsetPx = -35; // increase/decrease to move compass right/left
        const containerHeight = this.container?.getBoundingClientRect
            ? this.container.getBoundingClientRect().height
            : window.innerHeight;
        const cubeRect = this.cubeRenderer.domElement.getBoundingClientRect();
        const cubeSize = cubeRect.height || this.cubeRenderer.domElement.clientHeight || 210;
        const top = Math.max(0, containerHeight - cubeSize);
        this.cubeRenderer.domElement.style.top = top.toString() + 'px';
        this.cubeRenderer.domElement.style.left = 'auto';
        this.cubeRenderer.domElement.style.right = `${compassRightOffsetPx}px`;
        this.cubeRenderer.render(this.cubeScene, this.cubeCamera);
    }

    // Return objects needed externally by SceneManager or Main
    getCubeSceneObjects() {
        return {
            cubeScene: this.cubeScene,
            cubeCamera: this.cubeCamera,
            cubeRenderer: this.cubeRenderer,
            viewCube: this.viewCube,
            compass: this.compassRing,
            handleResize: this.handleResize,
            setContainer: (container) => {
                this.container = container || null;
                this.handleResize();
            }
        };
    }
}
